import React from 'react';
import { Card, Input, Tag } from 'antd-mobile';
import { DeleteOutline } from 'antd-mobile-icons';

// Дизайн-токены раздела «Тест» (Госзадание GO)
export const T = {
  text: '#1c1c1e',
  label: '#8a8a8e',
  hint: '#aeaeb2',
  divider: '#f0f0f2',
  subBlockBg: '#f7f8fa',
  inputBorder: '#e5e5ea',
  success: '#52c41a',
  danger: '#ff4d4f',
  primary: '#1677ff',
};

export const cardStyle = {
  borderRadius: 14,
  boxShadow: '0 1px 4px rgba(0, 0, 0, 0.06)',
};

export const dateInputStyle = {
  width: '100%',
  padding: '9px 12px',
  fontSize: 15,
  border: 'none',
  borderRadius: 8,
  background: T.subBlockBg,
  boxSizing: 'border-box',
  color: T.text,
};

export function statusTag(status) {
  if (status === 'completed') return <Tag color="success">Выполнено</Tag>;
  if (status === 'cancelled') return <Tag color="danger">Отменено</Tag>;
  return <Tag color="primary">Активно</Tag>;
}

// Лейбл поля + контрол + необязательная подсказка
export function FieldLabel({ label, hint, children, style }) {
  return (
    <div style={{ marginBottom: 16, ...style }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.label, marginBottom: 6 }}>{label}</div>
      {children}
      {hint && (
        <div style={{ fontSize: 12, color: T.hint, marginTop: 5, lineHeight: 1.35 }}>{hint}</div>
      )}
    </div>
  );
}

// Карточка-секция с заголовком, необязательной иконкой и элементом справа
export function SectionCard({ title, icon, extra, children, style }) {
  return (
    <Card style={{ ...cardStyle, marginTop: 12, ...style }}>
      {title && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              fontSize: 15,
              fontWeight: 600,
              color: T.text,
            }}
          >
            {icon && (
              <span style={{ display: 'flex', fontSize: 17, color: T.primary }}>{icon}</span>
            )}
            {title}
          </div>
          {extra}
        </div>
      )}
      {children}
    </Card>
  );
}

// Вложенный блок (оборудование, конструкция) на серой подложке
export function SubBlock({ title, onDelete, children }) {
  return (
    <div style={{ background: T.subBlockBg, borderRadius: 10, padding: 12, marginBottom: 10 }}>
      {(title || onDelete) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 10,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: T.label }}>{title}</div>
          {onDelete && (
            <DeleteOutline style={{ color: T.danger, fontSize: 18 }} onClick={onDelete} />
          )}
        </div>
      )}
      {children}
    </div>
  );
}

// Текстовый инпут в едином стиле (по умолчанию — на белой карточке; внутри SubBlock передавать style={{ background: '#fff' }})
export function TextInput({ style, ...rest }) {
  return (
    <Input
      {...rest}
      style={{
        background: T.subBlockBg,
        borderRadius: 8,
        padding: '9px 12px',
        '--font-size': '15px',
        ...style,
      }}
    />
  );
}

// Поле-ячейка: лейбл внутри блока, значение под ним (light — белая ячейка для SubBlock)
export function CellField({ label, hint, icon, light, children, style }) {
  return (
    <div style={{ marginBottom: 10, ...style }}>
      <div
        style={{
          background: light ? '#fff' : T.subBlockBg,
          border: light ? `1px solid ${T.inputBorder}` : 'none',
          borderRadius: 10,
          padding: '7px 12px 9px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 5,
            fontSize: 11,
            fontWeight: 500,
            color: T.label,
            marginBottom: 3,
          }}
        >
          {icon && <span style={{ display: 'flex', fontSize: 13 }}>{icon}</span>}
          {label}
        </div>
        {children}
      </div>
      {hint && (
        <div style={{ fontSize: 12, color: T.hint, marginTop: 5, lineHeight: 1.35 }}>{hint}</div>
      )}
    </div>
  );
}

// Прозрачный инпут для использования внутри CellField
export function CellInput({ style, ...rest }) {
  return (
    <Input
      {...rest}
      style={{ background: 'transparent', padding: 0, '--font-size': '15px', ...style }}
    />
  );
}

// Нативный date-инпут внутри CellField
export const cellDateStyle = {
  width: '100%',
  padding: 0,
  fontSize: 15,
  border: 'none',
  borderRadius: 0,
  background: 'transparent',
  boxSizing: 'border-box',
  color: T.text,
};

// Варианты выбора крупными блоками (вместо мелких радиокнопок)
export function ChoiceBlocks({ value, options, onChange }) {
  return (
    <div>
      {(options || []).map((opt) => {
        const active = value === opt;
        return (
          <div
            key={opt}
            onClick={() => onChange(opt)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '11px 12px',
              marginBottom: 8,
              borderRadius: 10,
              cursor: 'pointer',
              border: `1.5px solid ${active ? T.primary : T.inputBorder}`,
              background: active ? '#f0f6ff' : '#fff',
              fontSize: 14,
              fontWeight: active ? 600 : 400,
              color: active ? T.primary : T.text,
            }}
          >
            <span
              style={{
                width: 18,
                height: 18,
                borderRadius: '50%',
                border: `2px solid ${active ? T.primary : '#c7c7cc'}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              {active && (
                <span
                  style={{ width: 8, height: 8, borderRadius: '50%', background: T.primary }}
                />
              )}
            </span>
            {opt}
          </div>
        );
      })}
    </div>
  );
}
